// The selection-mode controller: it syncs the toggle buttons to the current selection, drives the
// "Formus Recommended" cards, and tells the manual-selection state machine (stemManualSelection.ts)
// when to seed or clear itself. Composed into useStemOverlayViewModel.
import { computed, watch } from 'vue';
import { watchImmediate } from '@vueuse/core';
import type { Ref } from 'vue';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import { buildSizeItems, formatStemName } from './stemSelectableItems';
import {
    headForRecommendedStem,
    isHeadMatchingRecommendedStem,
    type RecommendedHeadQuery,
} from './stemRecommendation';
import type { RecommendedStemCard, SelectableStem, SelectionMode, SizeItem } from '../stem.types';
import type { StemConstructs } from './stemConstructs';
import type { StemFiltering } from './stemFiltering';
import type { StemManualSelection } from './stemManualSelection';

type StemSelectionModeDeps = {
    planner: PlannerStore;
    constructs: StemConstructs;
    filtering: StemFiltering;
    manual: StemManualSelection;
    selectionMode: Ref<SelectionMode>;
};

const buildRecommendedCards = (
    stems: SelectableStem[],
    isSelected: (stem: SelectableStem) => boolean,
): RecommendedStemCard[] =>
    stems.map((stem) => ({ stem, ...formatStemName(stem), selected: isSelected(stem) }));

export const useStemSelectionMode = (deps: StemSelectionModeDeps) => {
    const { planner, constructs, filtering, manual, selectionMode } = deps;

    const recommendedQuery = (): RecommendedHeadQuery => ({
        catalogHeads: planner.catalog?.heads ?? null,
        templateHeadUrl: planner.template?.headUrl ?? null,
        selectedCupSize: planner.selectedCup?.outerDiameter ?? null,
        dualMobility: planner.dualMobility,
    });

    const isSelectedRecommendedStem = (stem: SelectableStem): boolean =>
        constructs.selectedStem.value === stem.stemUrl &&
        isHeadMatchingRecommendedStem(stem, constructs.selectedHead.value, recommendedQuery());

    const clickRecommendedStem = (stem: SelectableStem) => {
        constructs.selectedStem.value = stem.stemUrl;
        constructs.selectedHead.value = headForRecommendedStem(stem, recommendedQuery());
    };

    const isRecommendedSelected = (): boolean =>
        !!constructs.selectedStem.value &&
        !!constructs.selectedHead.value &&
        filtering.recommendedStems.value.some(isSelectedRecommendedStem);

    // Point both toggles at the current selection: preferred/alternative system, recommended/manual mode.
    const refreshToggleButtons = () => {
        filtering.selectedStemSystem.value = constructs.isSelectedStemSystemPreferred.value
            ? 'preferred'
            : 'alternative';
        selectionMode.value = isRecommendedSelected() ? 'recommended' : 'manual';
    };

    // Manual fields follow the mode/system: seed them in manual mode, clear them in recommended.
    const syncManualToMode = async () => {
        if (selectionMode.value === 'manual') await manual.reinitialise();
        else manual.clear();
    };

    watchImmediate(selectionMode, syncManualToMode);
    watchImmediate(filtering.selectedStemSystem, syncManualToMode);

    // The template was replaced under us: re-sync the toggles, then re-seed the manual fields if
    // the new template still resolves to manual mode.
    const resyncToTemplate = async () => {
        refreshToggleButtons();
        if (selectionMode.value === 'manual') await manual.reinitialise();
    };

    // Stem reset, and a new case. The mode and system watchers above cannot cover the case switch:
    // they are change-gated, and a new case commonly resolves to the same mode, so only an
    // explicit trigger on the case always fires.
    watch(() => planner.stemResetCount, resyncToTemplate);
    watch(() => planner.case?.caseId, resyncToTemplate);

    // Returning from set-targets → re-sync the toggles to the (possibly re-ranked) selection.
    watchImmediate(
        () => planner.currentOperation,
        (operation, previous) => {
            if (operation === null && previous === 'set-targets') refreshToggleButtons();
        },
    );

    const recommendedStemCards = computed<RecommendedStemCard[]>(() =>
        buildRecommendedCards(filtering.recommendedStems.value, isSelectedRecommendedStem),
    );

    return {
        sizeItems: computed<SizeItem[]>(() => buildSizeItems(manual.selectableSizes.value)),
        isRecommendedMode: computed<boolean>(() => selectionMode.value === 'recommended'),
        isManualMode: computed<boolean>(() => selectionMode.value === 'manual'),
        showSelectionNote: computed<boolean>(
            () => filtering.hasRecommendedStems.value || selectionMode.value === 'manual',
        ),
        recommendedStemCards,
        isSelectedRecommendedStem,
        clickRecommendedStem,
        refreshToggleButtons,
    };
};
